import * as yup from 'yup';
import { SPLIT_CASE_CONDITION_FULL } from '_utils/constant';

const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i;

export const step1Schema = yup.object().shape({
  groupId: yup.mixed().required('Please select a group'),
  title: yup
    .string()
    .required('Event title is required')
    .min(1, 'Title is required')
    .max(256, 'Title must not exceed 256 characters'),
  description: yup.string().nullable().max(10240, 'Description is too long'),
  location: yup.string().nullable().max(256, 'Location must not exceed 256 characters'),
  closingDate: yup.string().required('Closing date is required'),
  collectionDate: yup
    .string()
    .required('Collection date is required')
    .test('is-after-closing', 'Collection date must be on or after closing date', function checkCollectionDate(value) {
      const { closingDate } = this.parent;
      if (!closingDate || !value) return true;
      return new Date(value) >= new Date(closingDate);
    }),
  timeFrom: yup.string().nullable(),
  timeTo: yup
    .string()
    .nullable()
    .test('is-after-time-from', 'Collection end time must be after start time', function checkCollectionTime(value) {
      const { timeFrom } = this.parent;
      if (!timeFrom || !value) return true;
      return value > timeFrom;
    }),
  adminCost: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? 0 : value))
    .min(0, 'Admin cost cannot be negative')
    .nullable(),
  extraFields: yup
    .array()
    .of(
      yup.object().shape({
        name: yup
          .string()
          .nullable()
          .test('is-name-required', 'Label is required when URL is provided', function checkName(nameVal) {
            const { value } = this.parent || {};
            const hasValue = Boolean(value && value.trim());
            const hasName = Boolean(nameVal && nameVal.trim());
            if (hasValue && !hasName) {
              return this.createError({ message: 'Label is required when URL is provided' });
            }
            return true;
          }),
        value: yup
          .string()
          .nullable()
          .test('is-valid-url', 'Please enter a valid URL (e.g. https://example.com)', function checkUrl(urlVal) {
            const { name } = this.parent || {};
            const hasName = Boolean(name && name.trim());
            const val = urlVal ? urlVal.trim() : '';

            if (!val) {
              if (hasName) {
                return this.createError({ message: 'URL is required when label is provided' });
              }
              return true;
            }

            if (!urlPattern.test(val)) {
              return this.createError({ message: 'Please enter a valid URL (e.g. https://example.com)' });
            }

            return true;
          }),
      })
    )
    .nullable(),
});

export const step2Schema = yup.object().shape({
  items: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().required('Item name is required').max(256, 'Name too long'),
        price: yup
          .number()
          .typeError('Price must be a valid number')
          .required('Price is required')
          .min(0, 'Price cannot be negative'),
        description: yup.string().nullable().max(10240, 'Description too long'),
        maxQuantity: yup
          .mixed()
          .nullable()
          .test('is-valid-stock', function checkStock(value) {
            if (value === undefined || value === null || value === '') return true;

            const stockNum = Number(value);
            if (isNaN(stockNum) || stockNum <= 0) {
              return this.createError({ message: 'Available stock must be greater than 0' });
            }

            const orderCount = Number(this.parent?.orderCount || 0);
            if (orderCount > 0 && stockNum < orderCount) {
              return this.createError({
                message: `Available stock cannot be less than current order count (${orderCount})`,
              });
            }

            return true;
          }),
        limitPerOrder: yup
          .mixed()
          .nullable()
          .test('is-valid-limit', function checkLimit(value) {
            if (value === undefined || value === null || value === '') return true;

            const limitNum = Number(value);
            const stockNum = Number(this.parent?.maxQuantity || 1000);

            if (!isNaN(limitNum) && !isNaN(stockNum) && limitNum > stockNum) {
              return this.createError({
                message: 'Limit per order cannot exceed available stock',
              });
            }

            return true;
          }),
        splitCasePurchase: yup
          .object()
          .nullable()
          .shape({
            acknowledgementRequiredPayAfter: yup
              .boolean()
              .test(
                'is-ack-required',
                'You must acknowledge that enabling split case activates the Pay Later option',
                function checkAck(value) {
                  const parent = this.parent || {};
                  const isFull = Boolean(
                    parent.isFullCase ||
                    parent.fulfillCondition === SPLIT_CASE_CONDITION_FULL ||
                    parent.fulfilmentOption === 'full'
                  );
                  if (isFull && !value) {
                    return false;
                  }
                  return true;
                }
              ),
          }),
        subItems: yup
          .array()
          .of(
            yup.object().shape({
              type: yup.string().nullable(),
              selectionMode: yup.string().nullable(),
              required: yup.mixed().test(
               'is-required-for-quantities',
               'Required is mandatory for Choose Quantities',
               function checkRequired(value) {
                 const isTrackInventory = Boolean(this.parent?.trackInventory);
                 const isRequired = value === 1 || value === true || value === '1';
                 if (isTrackInventory && !isRequired) {
                  return false;
                 }
                 return true;
                }
              ),
              trackInventory: yup.boolean().nullable(),
              list: yup.array().of(
                yup.object().shape({
                  name: yup.string().nullable(),
                  price: yup.number().nullable(),
                  maxQty: yup.mixed().test(
                    'is-max-qty-required',
                    'Max quantity is required',
                    function checkMaxQty(value) {
                      const groupContext = this.from?.find(
                        (f) => f.value && 'trackInventory' in f.value
                      );
                      const trackInventory = groupContext
                        ? groupContext.value.trackInventory
                        : false;
                      if (trackInventory) {
                        return value !== undefined && value !== null && value !== '';
                      }
                      return true;
                    }
                  ),
                })
              ),
            })
          )
          .nullable(),
      })
    )
    .min(1, 'At least one product item is required'),
});

export const fullEventSchema = yup.object().shape({
  ...step1Schema.fields,
  ...step2Schema.fields,
});
